import { motion } from "framer-motion";
import { useEffect, useState } from "react";
import { Project, StrapiData } from "../types";
import { useRouter } from "../context/RouterContext";
import { ArrowLeft, Github, ExternalLink, Calendar } from "lucide-react";
import { api } from "../services/api";
import { useLocale } from "../context/LocaleContext";
import { ShareButton } from "../components/ShareButton";

export function ProjectPage() {
  const [project, setProject] = useState<Project | null>(null);
  const [loading, setLoading] = useState(true);
  const { params, navigate } = useRouter();
  const { locale } = useLocale();

  useEffect(() => {
    async function fetchProject() {
      if (!params.slug) {
        navigate("projects");
        return;
      }

      try {
        const response: StrapiData<Project[]> = await api.getProjectBySlug(
          params.slug,
          locale
        );
        if (response.data && response.data.length > 0) {
          const projectData = response.data[0];
          // Map the response to match our Project interface
          setProject({
            id: projectData.id,
            name: (projectData as any).name,
            slug: (projectData as any).slug,
            description: (projectData as any).description,
            tech_stack: (projectData as any).tech_stack || [],
            github_url: (projectData as any).github_url,
            demo_url: (projectData as any).demo_url,
            image_url: (projectData as any).thumbnail?.url || null,
            created_at: (projectData as any).createdAt,
          });
        } else {
          navigate("projects");
        }
      } catch (error) {
        console.error("Failed to fetch project:", error);
        navigate("projects");
      } finally {
        setLoading(false);
      }
    }

    fetchProject();
  }, [params.slug, locale, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-red-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!project) {
    return null;
  }

  return (
    <div className="min-h-screen bg-black">
      <div className="container mx-auto px-4 py-24">
        <motion.button
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          whileHover={{ x: -5 }}
          onClick={() => navigate("projects")}
          className="flex items-center gap-2 text-gray-400 hover:text-red-600 transition-colors mb-8"
        >
          <ArrowLeft className="w-5 h-5" />
          {locale === "tr-TR" ? "Projelere Geri Dön" : "Back to Projects"}
        </motion.button>

        <article className="max-w-4xl mx-auto">
          <motion.header
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-8"
          >
            <h1 className="text-4xl md:text-5xl font-bold mb-6 leading-tight">
              {project.name}
            </h1>

            <div className="flex flex-wrap items-center gap-4 text-gray-400 mb-6">
              {project.created_at && (
                <div className="flex items-center gap-2">
                  <Calendar className="w-5 h-5" />
                  <span>
                    {new Date(project.created_at).toLocaleDateString("en-US", {
                      year: "numeric",
                      month: "long",
                      day: "numeric",
                    })}
                  </span>
                </div>
              )}
            </div>

            <div className="flex flex-wrap gap-3 mb-6">
              {project.github_url && (
                <motion.a
                  href={project.github_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  whileHover={{ scale: 1.05 }}
                  className="flex items-center gap-2 px-4 py-2 bg-gray-900/50 border border-red-900/20 rounded-lg hover:border-red-600/50 transition-colors"
                >
                  <Github className="w-5 h-5" />
                  <span>View Source</span>
                </motion.a>
              )}
              {project.demo_url && (
                <motion.a
                  href={project.demo_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  whileHover={{ scale: 1.05 }}
                  className="flex items-center gap-2 px-4 py-2 bg-red-600 border border-red-600 rounded-lg hover:bg-red-700 transition-colors"
                >
                  <ExternalLink className="w-5 h-5" />
                  <span>Live Demo</span>
                </motion.a>
              )}
              <ShareButton
                title={project.name}
                url={`/projects/${project.slug}`}
                description={project.description}
              />
            </div>
          </motion.header>

          {/* Thumbnail image if available */}
          {project.image_url && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="mb-8 rounded-xl overflow-hidden border border-red-900/20"
            >
              <img
                src={`${
                  process.env.REACT_APP_STRAPI_URL ||
                  "https://devops.caglayagmur.dev/api"
                }${project.image_url}`}
                alt={project.name}
                className="w-full h-auto"
              />
            </motion.div>
          )}

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="prose prose-invert prose-red max-w-none"
          >
            <div className="bg-gray-900/50 border border-red-900/20 rounded-xl p-8 mb-8">
              <h2 className="text-2xl font-bold mb-4">Description</h2>
              <p className="text-lg text-gray-300 leading-relaxed whitespace-pre-wrap">
                {project.description}
              </p>
            </div>

            {/* Tech Stack */}
            {project.tech_stack && project.tech_stack.length > 0 && (
              <div className="bg-gray-900/50 border border-red-900/20 rounded-xl p-8">
                <h2 className="text-2xl font-bold mb-4">Tech Stack</h2>
                <div className="flex flex-wrap gap-2">
                  {project.tech_stack.map((tech, index) => (
                    <span
                      key={index}
                      className="px-3 py-1 text-sm font-medium bg-red-600/10 text-red-500 border border-red-600/20 rounded-full"
                    >
                      {tech}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </motion.div>
        </article>
      </div>
    </div>
  );
}
